import React, { createContext, useContext, useState, useEffect } from 'react';
import firebaseService, { DEMO_MODE } from '../services/FirebaseService';

// Create context
const AuthContext = createContext();

// Provider component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup demo user for demo mode
  useEffect(() => {
    setLoading(true);
    
    // In demo mode, use a fake user
    if (DEMO_MODE) {
      setCurrentUser({
        uid: 'demo-user-123',
        email: 'demo@example.com',
        displayName: 'Demo User'
      });
      setLoading(false);
      return;
    }
    
    // Otherwise use the real auth state
    const unsubscribe = firebaseService.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign in
  async function signIn(email, password) {
    if (DEMO_MODE) {
      // In demo mode, always succeed with a fake user
      setCurrentUser({
        uid: 'demo-user-123',
        email: email || 'demo@example.com',
        displayName: 'Demo User'
      });
      return { user: currentUser };
    }
    return firebaseService.signIn(email, password);
  }

  // Sign up
  async function signUp(email, password, displayName) {
    if (DEMO_MODE) {
      // In demo mode, always succeed with a fake user
      setCurrentUser({
        uid: 'demo-user-123',
        email: email || 'demo@example.com',
        displayName: displayName || 'Demo User'
      });
      return { user: currentUser };
    }
    return firebaseService.signUp(email, password, displayName);
  }

  // Sign out
  async function signOut() {
    if (DEMO_MODE) {
      // In demo mode, don't actually sign out
      window.location.href = '/';
      return Promise.resolve();
    }
    await firebaseService.signOut();
    // No need to redirect here as it's handled in FirebaseService
  }

  // Google sign-in
  async function signInWithGoogle() {
    if (DEMO_MODE) {
      // In demo mode, always succeed with a fake user
      setCurrentUser({
        uid: 'demo-user-123',
        email: 'demo@example.com',
        displayName: 'Demo User'
      });
      return { user: currentUser };
    }
    return firebaseService.signInWithGoogle();
  }

  const value = {
    currentUser,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth
export function useAuth() {
  return useContext(AuthContext);
} 